import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
  alertError,
  alertSuccess,
  downloadJson,
} from '../api/ticketsApi';
import { apiPath } from '../../../config/apiBase';
import { ModuleShell, SkeletonBlock } from '../../../components/module-ui';
import { notifySuccess, notifyError } from '../../../lib/notify';

const TEMPLATES = `${API}/form-templates`;
const PRODUCTS = apiPath('/api/products/active');

const FIELD_TYPES = [
  { value: 'text', label: 'Text field' },
  { value: 'textarea', label: 'Description (long text)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio buttons' },
  { value: 'checkbox', label: 'Checkbox' },
];
const HAS_OPTIONS = ['select', 'radio', 'checkbox'];

const slug = (s) => (s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const blankField = (type) => ({
  label: '', field_key: '', field_type: type, required: false,
  full_width: false, placeholder: '', help_text: '', options: [],
  source_type: 'static', data_source_id: '', depends_on: '',
});

export default function FormBuilderPage() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template being created/edited
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataSources, setDataSources] = useState([]);
  const importRef = useRef(null);

  useEffect(() => {
    axios.get(PRODUCTS, authHeaders()).then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
    axios.get(`${API}/data-sources`, authHeaders()).then((r) => setDataSources(r.data || [])).catch(() => setDataSources([]));
  }, []);

  const loadTemplates = async (pid) => {
    if (!pid) { setTemplates([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${TEMPLATES}?product_id=${pid}`, authHeaders());
      setTemplates(r.data || []);
    } catch (e) { setErr('Failed to load templates'); } finally { setLoading(false); }
  };

  useEffect(() => { loadTemplates(productId); setEditing(null); }, [productId]);

  const flash = (m) => { notifySuccess(m); };

  // ---- editor helpers ----
  const startNew = () => setEditing({ name: '', description: '', fields: [] });
  const editTemplate = (t) => {
    const copy = JSON.parse(JSON.stringify(t));
    copy.fields = (copy.fields || []).map((f) => ({ ...f, _keyLocked: !!f.field_key }));
    setEditing(copy);
  };
  const addField = (type) => setEditing((e) => ({ ...e, fields: [...e.fields, blankField(type)] }));
  const patchField = (i, patch) => setEditing((e) => ({
    ...e, fields: e.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)),
  }));
  const removeField = (i) => setEditing((e) => ({ ...e, fields: e.fields.filter((_, idx) => idx !== i) }));
  const moveField = (i, dir) => setEditing((e) => {
    const j = i + dir;
    if (j < 0 || j >= e.fields.length) return e;
    const fields = [...e.fields];
    [fields[i], fields[j]] = [fields[j], fields[i]];
    return { ...e, fields };
  });

  const save = async () => {
    setErr(null);
    if (!editing.name.trim()) { setErr('Template name is required'); return; }
    if (!editing.fields.length) { setErr('Add at least one field'); return; }
    if (editing.fields.some((f) => !f.label.trim())) { setErr('Every field needs a label'); return; }
    const payload = {
      product_id: productId,
      name: editing.name,
      description: editing.description || null,
      fields: editing.fields.map((f) => ({
        ...f,
        options: HAS_OPTIONS.includes(f.field_type)
          ? (Array.isArray(f.options) ? f.options : String(f.options).split('\n'))
              .map((o) => o.trim()).filter(Boolean)
          : [],
      })),
    };
    try {
      if (editing.template_id) {
        await axios.put(`${TEMPLATES}/${editing.template_id}`, payload, authHeaders());
        flash('Template updated');
      } else {
        await axios.post(TEMPLATES, payload, authHeaders());
        flash('Template created');
      }
      setEditing(null);
      loadTemplates(productId);
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
  };

  const activate = async (id) => {
    try { await axios.post(`${TEMPLATES}/${id}/activate`, {}, authHeaders()); flash('Template activated'); loadTemplates(productId); }
    catch { setErr('Activate failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await axios.delete(`${TEMPLATES}/${id}`, authHeaders()); flash('Template deleted'); loadTemplates(productId); }
    catch { setErr('Delete failed'); }
  };

  const productName = () => products.find((p) => p.product_id === productId)?.name || productId;
  const exportTemplates = async () => {
    setErr(null);
    try {
      const r = await axios.get(`${TEMPLATES}/export?product_id=${productId}`, authHeaders());
      const stamp = new Date().toISOString().slice(0, 10);
      downloadJson(`form-templates_${productName()}_${stamp}.json`, r.data);
      flash(`Exported ${r.data.count || 0} template(s)`);
    } catch { setErr('Export failed'); }
  };
  const importTemplates = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!productId) {
      setErr('Select a product before importing — import copies into the selected product.');
      return;
    }
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // mode=copy keeps the exported product's templates; creates new ids on the target.
      const r = await axios.post(
        `${TEMPLATES}/import?product_id=${productId}&mode=copy`,
        fd,
        authHeaders(),
      );
      const d = r.data || {};
      flash(`Copied into ${productName()}: ${d.created || 0} new, ${d.updated || 0} updated${d.skipped ? `, ${d.skipped} skipped` : ''}`);
      if (d.errors?.length) setErr(d.errors.join(' · '));
      loadTemplates(productId);
    } catch (e2) { setErr(e2.response?.data?.detail || 'Import failed'); }
  };

  return (
    <ModuleShell
      title="Form Builder"
      subtitle="Design custom App-Support ticket forms per product. The active template loads automatically on New Ticket when its product is selected."
    >
    <div style={{ maxWidth: '960px' }}>
      {err && <div style={alertError}>{typeof err === 'string' ? err : JSON.stringify(err)}</div>}

      <div style={{ marginBottom: '8px', maxWidth: '420px' }}>
        <label style={labelStyle}>Product</label>
        <select style={fieldStyle} value={productId} onChange={(e) => setProductId(e.target.value)}>
          <option value="">Select a product…</option>
          {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
        </select>
      </div>

      {productId && !editing && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Templates</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={btnSecondary} onClick={exportTemplates} disabled={!templates.length} title="Download this product's templates as JSON">Export</button>
              <button style={btnSecondary} onClick={() => importRef.current?.click()} title="Copy templates from a JSON export into this product (source product is kept)">Import / Copy</button>
              <button style={btnPrimary} onClick={startNew}>+ New Template</button>
              <input ref={importRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={importTemplates} />
            </div>
          </div>
          {loading ? <SkeletonBlock rows={3} /> : (
            <div style={{ display: 'grid', gap: '10px' }}>
              {templates.length === 0 && <p style={{ color: '#9CA3AF' }}>No templates yet. Create one — it becomes active automatically.</p>}
              {templates.map((t) => (
                <div key={t.template_id} style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <div>
                    <div style={{ fontWeight: '600' }}>
                      {t.name}
                      {t.active && <span style={{ marginLeft: '8px', fontSize: '11px', background: '#DCFCE7', color: '#166534', padding: '2px 8px', borderRadius: '999px' }}>ACTIVE</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>{t.fields?.length || 0} field(s){t.description ? ` · ${t.description}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {!t.active && <button style={btnSecondary} onClick={() => activate(t.template_id)}>Set Active</button>}
                    <button style={btnSecondary} onClick={() => editTemplate(t)}>Edit</button>
                    <button style={{ ...btnSecondary, color: '#DC2626' }} onClick={() => remove(t.template_id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '18px' }}>
          <div style={{ display: 'grid', gap: '8px', marginBottom: '8px' }}>
            <div>
              <label style={labelStyle}>Template name *</label>
              <input style={fieldStyle} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. NBFC Onboarding Issue" />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <input style={fieldStyle} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
          </div>

          <div style={{ marginBottom: '6px' }}>
            <div style={labelStyle}>Add a component</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {FIELD_TYPES.map((ft) => (
                <button key={ft.value} style={btnSecondary} onClick={() => addField(ft.value)}>+ {ft.label}</button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            {editing.fields.map((f, i) => (
              <div key={i} style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px', background: '#F9FAFB' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    {FIELD_TYPES.find((t) => t.value === f.field_type)?.label || f.field_type}
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button style={miniBtn} onClick={() => moveField(i, -1)} title="Move up">↑</button>
                    <button style={miniBtn} onClick={() => moveField(i, 1)} title="Move down">↓</button>
                    <button style={{ ...miniBtn, color: '#DC2626' }} onClick={() => removeField(i)} title="Remove">✕</button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={labelStyle}>Label *</label>
                    <input style={fieldStyle} value={f.label} onChange={(e) => patchField(i, { label: e.target.value, field_key: f._keyLocked ? f.field_key : slug(e.target.value) })} />
                    {f.field_key && <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>key: {f.field_key}</div>}
                  </div>
                  <div>
                    <label style={labelStyle}>Type</label>
                    <select style={fieldStyle} value={f.field_type} onChange={(e) => patchField(i, { field_type: e.target.value })}>
                      {FIELD_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Placeholder</label>
                    <input style={fieldStyle} value={f.placeholder || ''} onChange={(e) => patchField(i, { placeholder: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>Help text</label>
                    <input style={fieldStyle} value={f.help_text || ''} onChange={(e) => patchField(i, { help_text: e.target.value })} />
                  </div>
                  {HAS_OPTIONS.includes(f.field_type) && (
                    <>
                      <div>
                        <label style={labelStyle}>Options source</label>
                        <select style={fieldStyle} value={f.source_type || 'static'} onChange={(e) => patchField(i, { source_type: e.target.value })}>
                          <option value="static">Static list</option>
                          <option value="datasource">Data source (API / dynamic)</option>
                        </select>
                      </div>
                      {(f.source_type || 'static') === 'datasource' ? (
                        <>
                          <div>
                            <label style={labelStyle}>Data source</label>
                            <select style={fieldStyle} value={f.data_source_id || ''} onChange={(e) => patchField(i, { data_source_id: e.target.value })}>
                              <option value="">Select…</option>
                              {dataSources.map((ds) => <option key={ds.data_source_id} value={ds.data_source_id}>{ds.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={labelStyle}>Depends on (cascade parent)</label>
                            <select style={fieldStyle} value={f.depends_on || ''} onChange={(e) => patchField(i, { depends_on: e.target.value })}>
                              <option value="">None (loads on open)</option>
                              {editing.fields.filter((o, j) => j !== i && o.field_key && HAS_OPTIONS.includes(o.field_type))
                                .map((o) => <option key={o.field_key} value={o.field_key}>{o.label || o.field_key}</option>)}
                            </select>
                            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
                              Its value is sent to the data source as {'{{' + (f.depends_on || 'parent_key') + '}}'}.
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <label style={labelStyle}>Options (one per line)</label>
                          <textarea style={{ ...fieldStyle, minHeight: '70px' }}
                            value={Array.isArray(f.options) ? f.options.join('\n') : (f.options || '')}
                            onChange={(e) => patchField(i, { options: e.target.value.split('\n') })}
                            placeholder={'Option 1\nOption 2'} />
                        </div>
                      )}
                    </>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <input type="checkbox" checked={f.required} onChange={(e) => patchField(i, { required: e.target.checked })} /> Required
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <input type="checkbox" checked={f.full_width} onChange={(e) => patchField(i, { full_width: e.target.checked })} /> Full width
                  </label>
                </div>
              </div>
            ))}
            {editing.fields.length === 0 && <p style={{ color: '#9CA3AF' }}>No fields yet — add components above.</p>}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            <button style={btnPrimary} onClick={save}>{editing.template_id ? 'Save changes' : 'Create template'}</button>
            <button style={btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

const miniBtn = {
  border: '1px solid #D1D5DB', background: '#fff', borderRadius: '6px',
  width: '28px', height: '28px', cursor: 'pointer', fontSize: '12px',
};
